/**
 * @fileoverview `getCityAnchor` — translates a `CityCode` into the geo anchor
 * the discover wire format expects.
 *
 * Field naming gotcha: `@loci/city-editions` uses `lon` on `CityCenter`, but
 * `GET /api/v1/events/discover` (and `DiscoverEventsQueryArgs`) uses `lng` to
 * match `DiscoverEvent.lng`. We rename here at the boundary so consumers
 * can spread the result directly into the wire args. See
 * `docs/architecture/web-discover-surface.md` "Backend compatibility".
 *
 * @since WEB-263
 */
import { CITY_EDITIONS, type CityCode } from '@loci/city-editions';

/**
 * Geo anchor shape consumed by `useDiscoverEventsQuery` /
 * `useDiscoverFacetsQuery`. Spread directly into `DiscoverEventsQueryArgs`.
 */
export interface CityAnchor {
  lat: number;
  lng: number;
  radiusKm: number;
}

/**
 * Resolve a `CityCode` to its discover geo anchor. Renames the registry's
 * `lon` field to the API's `lng` field so downstream wire-arg builders can
 * spread the result without further translation.
 *
 * Falls back to the CLT anchor when `cityCode` is in `CityCode` but not yet
 * registered (CHI / MIA / NYC editions land in later milestones). The proxy
 * already restricts inbound `/[city]/discover` requests to registered codes,
 * so this fallback only fires in unit tests that pass an upcoming code.
 */
export function getCityAnchor(cityCode: CityCode): CityAnchor {
  const edition = CITY_EDITIONS[cityCode as keyof typeof CITY_EDITIONS] ?? CITY_EDITIONS.CLT;
  // COR-155: geography lives on the city record; the edition carries it.
  const { center, radiusKm } = edition.geography.defaultAnchor;
  return { lat: center.lat, lng: center.lon, radiusKm };
}
